import { useRef, useEffect, useState } from 'react';
import { getWeekDays, isToday, isMultiDayEvent, isEventPast } from '../../utils/calendar';
import { formatDate } from '../../utils/time';
import { CalendarEvent } from '../../types/calendar';
import { getDesignTokens } from '../../utils/designTokens';
import EventCard from './EventCard';

interface WeekViewProps {
  date: Date;
  events: CalendarEvent[];
  onDateClick?: (date: Date, hour?: number) => void;
  onEventClick?: (event: CalendarEvent) => void;
  isDarkMode?: boolean;
}

interface PositionedEvent {
  event: CalendarEvent;
  dayIndex: number; // Starting day index
  daySpan: number; // Number of days this event spans (for multi-day events)
  top: number; // Percentage from top of day (0-100)
  height: number; // Percentage of day height (0-100)
  left: number; // Percentage from left for overlapping events (0-100)
  width: number; // Percentage width for overlapping events (0-100)
  colSpan: number; // Number of day columns this event spans
}

export default function WeekView({
  date,
  events,
  onDateClick,
  onEventClick,
  isDarkMode = false,
}: WeekViewProps) {
  // Validate date to prevent NaN issues
  if (!date || isNaN(date.getTime())) {
    return <div>Invalid date</div>;
  }
  
  const weekDays = getWeekDays(date);
  
  // Validate weekDays to ensure all dates are valid
  if (!weekDays || weekDays.length === 0 || weekDays.some(day => isNaN(day.year) || isNaN(day.month) || isNaN(day.day))) {
    return <div>Error calculating week days</div>;
  }
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToCurrentTimeRef = useRef(false);
  const explanationRef = useRef<HTMLDivElement>(null);
  const [columnPositions, setColumnPositions] = useState<{ left: number; width: number }[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showHoursExplanation, setShowHoursExplanation] = useState(false);

  // Measure header column positions to match exactly
  useEffect(() => {
    if (!headerRef.current) return;
    
    // Use a small delay to ensure layout is complete
    const timeoutId = setTimeout(() => {
      if (!headerRef.current) return;
      
      const header = headerRef.current;
      // Get all columns (Time + 7 days = 8 total)
      const allColumns = Array.from(header.children) as HTMLElement[];
      
      if (allColumns.length === 0) return;

      const containerRect = header.getBoundingClientRect();
      const positions = allColumns.map((col) => {
        const rect = col.getBoundingClientRect();
        return {
          left: rect.left - containerRect.left, // Use absolute pixel positions
          width: rect.width,
        };
      });
      
      setColumnPositions(positions);
    }, 10);
    
    return () => clearTimeout(timeoutId);
  }, [weekDays]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Close explanation when clicking outside
  useEffect(() => {
    if (!showHoursExplanation) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (explanationRef.current && !explanationRef.current.contains(event.target as Node)) {
        setShowHoursExplanation(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHoursExplanation]);

  // Helper function to check if an event is a whole-day event
  const isWholeDayEvent = (event: CalendarEvent, day: Date): boolean => {
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day);
    dayEnd.setHours(23, 59, 59, 999);
    
    // Check if event overlaps with this day
    if (event.startTime > dayEnd || event.endTime < dayStart) {
      return false;
    }
    
    // Check if event encompasses the whole day
    const eventStart = new Date(event.startTime);
    const eventEnd = new Date(event.endTime);
    
    // Event starts at or before midnight and ends at or after 23:59
    const startsAtMidnight = eventStart.getHours() === 0 && eventStart.getMinutes() === 0;
    const endsAtEndOfDay = eventEnd.getHours() === 23 && eventEnd.getMinutes() === 59;
    const endsAfterMidnight = eventEnd.getHours() === 0 && eventEnd.getMinutes() === 0 && eventEnd > dayEnd;
    
    // Also check if it's a multi-day event that fully covers this day
    if (eventStart <= dayStart && (eventEnd >= dayEnd || endsAfterMidnight)) {
      return true;
    }
    
    return startsAtMidnight && (endsAtEndOfDay || endsAfterMidnight);
  };

  // Filter events for this week
  const weekStart = new Date(weekDays[0].date);
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekDays[weekDays.length - 1].date);
  weekEnd.setHours(23, 59, 59, 999);

  const weekEvents = events.filter(event => {
    // event.startTime and event.endTime are already Date objects from the store
    const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
    const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
    // Event overlaps if: eventStart <= weekEnd AND eventEnd >= weekStart
    // Use getTime() for reliable comparison
    const overlaps = eventStart.getTime() <= weekEnd.getTime() && eventEnd.getTime() >= weekStart.getTime();
    return overlaps;
  });
  
  // Deduplicate events by ID to prevent duplicate rendering (safety measure)
  const uniqueWeekEvents = weekEvents.filter((event, index, self) => 
    index === self.findIndex(e => e.id === event.id)
  );

  // Collect all multi-day events and all-day events (both single-day and multi-day)
  // They will all be positioned in the header using the same mechanics
  const multiDayEvents: CalendarEvent[] = [];
  
  uniqueWeekEvents.forEach(event => {
    // Skip if already added
    if (multiDayEvents.find(e => e.id === event.id)) return;
    
    const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
    const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
    
    // Check if this event overlaps with any day in the week
    const overlapsWeek = weekDays.some(day => {
      const dayStart = new Date(day.date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(day.date);
      dayEnd.setHours(23, 59, 59, 999);
      return eventStart.getTime() <= dayEnd.getTime() && eventEnd.getTime() >= dayStart.getTime();
    });
    
    if (!overlapsWeek) return;
    
    // Include if it's a multi-day event OR if it's an all-day event (single-day or multi-day)
    const isMultiDay = isMultiDayEvent(event.startTime, event.endTime);
    const isAllDay = event.isAllDay === true || event.isImportantDate;
    
    // Check if it's a whole-day event on any day in the week
    const isWholeDayOnAnyDay = weekDays.some(day => isWholeDayEvent(event, day.date));
    
    if (isMultiDay || isAllDay || isWholeDayOnAnyDay) {
      multiDayEvents.push(event);
    }
  });

  // Calculate maximum number of overlapping multi-day events to determine header height
  // Group multi-day events by their day spans to find overlaps
  const maxOverlappingMultiDayEvents = (() => {
    if (multiDayEvents.length === 0) return 0;
    
    // For each day in the week, count how many multi-day events overlap with it
    let maxOverlap = 0;
    weekDays.forEach((day) => {
      const dayStart = new Date(day.date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(day.date);
      dayEnd.setHours(23, 59, 59, 999);
      
      let overlapCount = 0;
      multiDayEvents.forEach(event => {
        const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
        const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
        
        if (eventStart.getTime() <= dayEnd.getTime() && eventEnd.getTime() >= dayStart.getTime()) {
          overlapCount++;
        }
      });
      
      maxOverlap = Math.max(maxOverlap, overlapCount);
    });
    
    return maxOverlap;
  })();
  
  // Calculate header height: base height + (number of stacked events * event height + gap)
  const baseHeaderHeight = 60; // Base height in pixels - enough for two-line header
  const eventHeight = 44; // Height of each multi-day/all-day event
  const eventGap = 4; // Gap between stacked events
  const headerHeight = maxOverlappingMultiDayEvents > 0
    ? baseHeaderHeight + (maxOverlappingMultiDayEvents * (eventHeight + eventGap))
    : baseHeaderHeight;

  // Filter out all-day events (both single-day and multi-day) from timed events
  // Also exclude events that are already in multiDayEvents to prevent double rendering
  const timedEvents = uniqueWeekEvents.filter(event => {
    // Skip if already in multiDayEvents
    if (multiDayEvents.some(e => e.id === event.id)) {
      return false;
    }
    // Check if this event is all-day on any day in the week
    return !weekDays.some(day => isWholeDayEvent(event, day.date));
  });

  // Calculate positions for timed events
  // For multi-day events, duplicate them on each day they span
  const positionedEvents: PositionedEvent[] = [];
  const dayDuration = 24 * 60; // 24 hours in minutes

  timedEvents.forEach(event => {
    const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
    const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);

    // Find all days this event spans
    const eventDays: number[] = [];
    weekDays.forEach((day, dayIndex) => {
      const dayStart = new Date(day.date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(day.date);
      dayEnd.setHours(23, 59, 59, 999);
      
      // Check if event overlaps with this day
      if (eventStart.getTime() <= dayEnd.getTime() && eventEnd.getTime() >= dayStart.getTime()) {
        eventDays.push(dayIndex);
      }
    });
    
    // Skip if event doesn't overlap with any day in this week
    if (eventDays.length === 0) return;
    
    // For multi-day events, create a single positioned event that spans all days
    // For single-day events, use the first (and only) day
    const startDayIndex = eventDays[0];
    const endDayIndex = eventDays[eventDays.length - 1];
    const daySpan = eventDays.length;
    
    const startDay = weekDays[startDayIndex];
    const startDayStart = new Date(startDay.date);
    startDayStart.setHours(0, 0, 0, 0);
    
    // Calculate start and end minutes for the event
    let startMinutes: number;
    if (eventStart.getTime() >= startDayStart.getTime()) {
      // Event starts on this day
      startMinutes = eventStart.getHours() * 60 + eventStart.getMinutes();
    } else {
      // Event started before this day
      startMinutes = 0;
    }

    const endDay = weekDays[endDayIndex];
    const endDayEnd = new Date(endDay.date);
    endDayEnd.setHours(23, 59, 59, 999);
    
    let endMinutes: number;
    if (eventEnd.getTime() <= endDayEnd.getTime()) {
      // Event ends on this day
      endMinutes = eventEnd.getHours() * 60 + eventEnd.getMinutes();
    } else {
      // Event continues past this day - calculate total minutes across all days
      endMinutes = (daySpan * 24 * 60);
    }

    const top = (startMinutes / dayDuration) * 100;
    const height = ((endMinutes - startMinutes) / dayDuration) * 100;

    // Check for overlapping events on the same day/time
    // Using <= and >= to include events that start/end at exactly the same time
    const overlappingEvents = positionedEvents.filter(pe => 
      pe.dayIndex === startDayIndex &&
      pe.top <= top + height &&
      pe.top + pe.height >= top
    );

    const groupSize = overlappingEvents.length + 1;
    
    // Add horizontal spacing between overlapping events
    const horizontalGap = 1; // 1% gap between side-by-side events
    const totalGap = (groupSize - 1) * horizontalGap;
    const availableWidth = 100 - totalGap;
    const eventWidth = availableWidth / groupSize;
    const left = (overlappingEvents.length * (eventWidth + horizontalGap));

    // Add vertical spacing: reduce height and adjust top to create gaps
    const verticalGap = 0.3; // 0.3% gap between vertically adjacent events
    const adjustedTop = top + (overlappingEvents.length * verticalGap);
    const adjustedHeight = Math.max(height - verticalGap, 0.5); // Ensure minimum height

    positionedEvents.push({
      event,
      dayIndex: startDayIndex,
      daySpan: daySpan,
      top: adjustedTop,
      height: adjustedHeight,
      left,
      width: eventWidth,
      colSpan: daySpan, // Span across all days
    });
  });

  // Get hours that have events
  const hoursWithEvents = new Set<number>();
  positionedEvents.forEach(({ event }) => {
    const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
    const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
    
    // Add all hours that the event spans (inclusive of start and end hours)
    const startHour = eventStart.getHours();
    const endHour = eventEnd.getHours();
    
    // Add start hour
    hoursWithEvents.add(startHour);
    
    // Add end hour if different from start
    if (endHour !== startHour) {
      hoursWithEvents.add(endHour);
    }
    
    // Add all hours in between
    for (let h = startHour + 1; h < endHour; h++) {
      hoursWithEvents.add(h);
    }
    
    // If event ends at the start of an hour (e.g., 10:00), we still want to show that hour
    // But if it ends at exactly the start of the next hour, we might not need the next hour
    // For now, let's be inclusive and add the end hour
  });

  // Convert to sorted array - if no events, show all hours
  const hours = hoursWithEvents.size > 0 
    ? Array.from(hoursWithEvents).sort((a, b) => a - b)
    : Array.from({ length: 24 }, (_, i) => i);

  // Check if hours are filtered (showing only hours with events)
  const isHoursFiltered = hours.length > 0 && hours.length < 24;

  // Calculate current time position (only for today)
  const isViewingToday = weekDays.some(day => isToday(day.date));
  let currentTimePosition: number | null = null;
  if (isViewingToday && hours.length > 0) {
    const today = weekDays.find(day => isToday(day.date));
    if (today) {
      const currentHour = currentTime.getHours();
      const currentMinute = currentTime.getMinutes();
      
      // Find which visible hour range we're in
      const hourIndex = hours.findIndex(h => h >= currentHour);
      if (hourIndex !== -1) {
        const minutesInHour = currentMinute;
        const gapPx = 4; // Fixed 4px gap between hours
        const baseHourHeight = 100; // Base height per hour in pixels
        const hourPositionPx = (hourIndex * baseHourHeight) + (hourIndex * gapPx);
        const offsetWithinHourPx = (minutesInHour / 60) * baseHourHeight;
        // Calculate contentHeight for this calculation
        const contentHeight = hours.length > 0 
          ? (hours.length * baseHourHeight) + ((hours.length - 1) * gapPx)
          : 2400;
        currentTimePosition = ((hourPositionPx + offsetWithinHourPx) / contentHeight) * 100;
      }
    }
  }

  // Scroll to current time ONLY on initial load (when hours or weekDays change, but not when currentTime updates)
  useEffect(() => {
    // Skip if we've already scrolled to current time
    if (hasScrolledToCurrentTimeRef.current) return;
    
    if (!scrollContainerRef.current || hours.length === 0) return;
    
    if (!isViewingToday) {
      hasScrolledToCurrentTimeRef.current = true; // Mark as done even if not viewing today
      return;
    }

    const today = weekDays.find(day => isToday(day.date));
    if (!today) {
      hasScrolledToCurrentTimeRef.current = true;
      return;
    }

    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    
    // Find which visible hour range we're in
    const hourIndex = hours.findIndex(h => h >= currentHour);
    if (hourIndex === -1) {
      hasScrolledToCurrentTimeRef.current = true;
      return;
    }
    
    const minutesInHour = currentMinute;
    const gapPx = 4; // Fixed 4px gap between hours
    const baseHourHeight = 100; // Base height per hour in pixels
    const hourPositionPx = (hourIndex * baseHourHeight) + (hourIndex * gapPx);
    const offsetWithinHourPx = (minutesInHour / 60) * baseHourHeight;
    // Calculate contentHeight for this calculation
    const contentHeight = hours.length > 0 
      ? (hours.length * baseHourHeight) + ((hours.length - 1) * gapPx)
      : 2400;
    const timePosition = ((hourPositionPx + offsetWithinHourPx) / contentHeight) * 100;
    
    // Scroll to center the current time (only once on initial load)
    const container = scrollContainerRef.current;
    const containerHeight = container.clientHeight;
    const scrollHeight = container.scrollHeight;
    const targetScroll = (timePosition / 100) * scrollHeight - (containerHeight / 2);
    
    container.scrollTo({
      top: Math.max(0, targetScroll),
      behavior: 'smooth'
    });
    
    // Mark as done so we don't scroll again
    hasScrolledToCurrentTimeRef.current = true;
  }, [hours, weekDays, isViewingToday]); // Removed currentTime from dependencies
  
  // Reset scroll flag when date or hours change significantly (new week loaded)
  useEffect(() => {
    hasScrolledToCurrentTimeRef.current = false;
  }, [date, hours.length]);

  const tokens = getDesignTokens(isDarkMode);
  const theme = {
    bg: isDarkMode ? 'bg-[#0f172a]/50' : 'bg-[#fffefb]/50',
    border: isDarkMode ? 'border-[rgba(107,114,128,0.3)]' : 'border-[rgba(217,119,111,0.25)]', // Peachy warm tone
    text: tokens.text.primary,
    textMuted: tokens.text.muted,
    today: isDarkMode 
      ? 'bg-[rgba(96,165,250,0.2)] border-[rgba(96,165,250,0.5)]' 
      : 'bg-[rgba(217,119,111,0.15)] border-[rgba(217,119,111,0.4)]',
    hourBorder: tokens.border.light,
    dayBorder: isDarkMode ? 'border-[rgba(107,114,128,0.3)]' : 'border-[rgba(217,119,111,0.25)]', // Peachy warm tone
      };

  // Calculate total content height needed
  // Use fixed pixel gap between hours instead of percentage
  const gapPx = 4; // Fixed 4px gap between hours
  const baseHourHeight = 100; // Base height per hour in pixels
  const contentHeight = hours.length > 0 
    ? (hours.length * baseHourHeight) + ((hours.length - 1) * gapPx)
    : 2400; // Fallback for 24 hours

  return (
    <div className="w-full h-full flex flex-col" style={{ height: '100%', overflow: 'hidden' }}>
      {/* Fixed headers - not scrollable */}
      <div className="flex-shrink-0 overflow-visible">
        {/* Week day headers with multi-day and all-day events */}
        <div ref={headerRef} className="flex gap-1 sm:gap-2 mb-1 sm:mb-2 bg-inherit relative">
        {/* Time column header - fixed width to match content */}
        <div 
          className={`${theme.textMuted} text-sm sm:text-base lg:text-lg font-bold py-1.5 sm:py-2 w-24 flex-shrink-0 flex flex-col items-center justify-center relative rounded-landing`}
          style={{ minHeight: `${headerHeight}px` }}
        >
          <div>Time</div>
          {isHoursFiltered && (
            <button
              onClick={() => setShowHoursExplanation(!showHoursExplanation)}
              className={`${theme.textMuted} text-xs sm:text-sm mt-1 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-sm-full hover:opacity-80 active:opacity-60 transition-opacity touch-manipulation`}
              aria-label="Why are some hours missing?"
            >
              <span className="text-sm sm:text-base">ℹ️</span>
            </button>
          )}
          {isHoursFiltered && showHoursExplanation && (
            <div 
              ref={explanationRef}
              className={`absolute top-full mt-2 left-0 right-0 z-50 ${theme.bg} ${theme.border} border-2 rounded-sm p-2 sm:p-3 shadow-lg`}
              style={{ minWidth: '200px' }}
            >
              <div className={`${theme.text} text-xs sm:text-sm leading-relaxed`}>
                <div className="font-semibold mb-1">Showing hours with events only</div>
                <div className="text-xs">Add events to see other time slots</div>
              </div>
              <button
                onClick={() => setShowHoursExplanation(false)}
                className={`${theme.textMuted} text-xs mt-2 underline hover:opacity-80 active:opacity-60 touch-manipulation`}
              >
                Close
              </button>
            </div>
          )}
        </div>
        {/* Day headers - flexible width */}
        {weekDays.map((day, dayIndex) => {
          const isDateToday = isToday(day.date);
          return (
            <div
              key={`${day.year}-${day.month}-${day.day}`}
              className={`text-center py-1.5 px-1 rounded-landing border-2 cursor-pointer touch-manipulation active:scale-95 flex-1 relative flex flex-col ${
                isDateToday
                  ? `${theme.today} font-semibold`
                  : `${theme.bg} ${theme.border} ${theme.text}`
              }`}
              style={{ minHeight: `${headerHeight}px` }}
              onClick={() => onDateClick?.(day.date)}
              data-day-index={dayIndex}
            >
              {/* Date information - two lines: day of month (smaller) and day of week (larger) */}
              <div className="flex-shrink-0 flex flex-col items-center gap-0.5">
                <div className={`text-xs sm:text-sm font-semibold ${theme.textMuted} leading-tight`}>
                  {day.day}
                </div>
                <div className={`text-base sm:text-lg lg:text-xl font-bold ${theme.text} leading-tight`}>
                  {formatDate(day.date, 'long').split(',')[0]}
                </div>
              </div>
              {/* Space for multi-day events - will be filled by absolutely positioned events */}
              {multiDayEvents.length > 0 && <div className="flex-1" style={{ minHeight: `${maxOverlappingMultiDayEvents * (eventHeight + eventGap)}px` }} />}
            </div>
          );
        })}
        
        {/* Multi-day events positioned over headers */}
        {multiDayEvents.map((event) => {
          // Find which days this event spans
          const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
          const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
          
          const eventDays: number[] = [];
          weekDays.forEach((day, dayIndex) => {
            const dayStart = new Date(day.date);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(day.date);
            dayEnd.setHours(23, 59, 59, 999);
            
            if (eventStart.getTime() <= dayEnd.getTime() && eventEnd.getTime() >= dayStart.getTime()) {
              eventDays.push(dayIndex);
            }
          });
          
          if (eventDays.length === 0) return null;
          
          const startDayIndex = eventDays[0];
          const endDayIndex = eventDays[eventDays.length - 1];
          const timeColumnWidth = 96; // w-24 = 96px
          const containerWidth = headerRef.current?.clientWidth || 0;
          
          // Get column positions for positioning (index 0 is Time column, so day columns start at index 1)
          const startColumnPos = columnPositions[startDayIndex + 1];
          const endColumnPos = columnPositions[endDayIndex + 1];
          
          // Calculate position and width - use measured positions if available, otherwise fallback
          let eventLeft: number;
          let eventWidth: number;
          
          if (startColumnPos && endColumnPos && containerWidth > 0) {
            // Use measured positions
            eventLeft = startColumnPos.left;
            eventWidth = (endColumnPos.left + endColumnPos.width) - startColumnPos.left;
          } else if (containerWidth > 0) {
            // Fallback calculation
            const availableWidth = containerWidth - timeColumnWidth;
            const dayColumnWidth = availableWidth / 7;
            const gapSize = containerWidth < 640 ? 4 : 8; // gap-1 (4px) or gap-2 (8px)
            
            eventLeft = timeColumnWidth + (startDayIndex * (dayColumnWidth + gapSize));
            eventWidth = (endDayIndex - startDayIndex + 1) * dayColumnWidth + (endDayIndex - startDayIndex) * gapSize;
          } else {
            // Can't calculate without container width
            return null;
          }
          
          // Skip if width is invalid
          if (eventWidth <= 0) return null;
          
          // Find overlapping events (events that span overlapping days)
          const overlappingEvents = multiDayEvents.filter((otherEvent, otherIndex) => {
            const otherEventIndex = multiDayEvents.findIndex(e => e.id === event.id);
            if (otherIndex >= otherEventIndex) return false; // Only check previous events
            const otherStart = otherEvent.startTime instanceof Date ? otherEvent.startTime : new Date(otherEvent.startTime);
            const otherEnd = otherEvent.endTime instanceof Date ? otherEvent.endTime : new Date(otherEvent.endTime);
            
            // Check if events overlap on any day
            return eventDays.some(dayIndex => {
              const dayStart = new Date(weekDays[dayIndex].date);
              dayStart.setHours(0, 0, 0, 0);
              const dayEnd = new Date(weekDays[dayIndex].date);
              dayEnd.setHours(23, 59, 59, 999);
              
              return otherStart.getTime() <= dayEnd.getTime() && otherEnd.getTime() >= dayStart.getTime();
            });
          });
          
          // Calculate vertical position (stack index)
          const stackIndex = overlappingEvents.length;
          const bottomPosition = 4 + (stackIndex * (eventHeight + eventGap));
          
          return (
            <div
              key={`multiday-${event.id}`}
              className="absolute"
              style={{
                left: `${eventLeft}px`,
                width: `${eventWidth}px`,
                top: 'auto',
                bottom: `${bottomPosition}px`,
                height: `${eventHeight}px`,
              }}
            >
              <EventCard
                event={event}
                onEventClick={onEventClick}
                isDarkMode={isDarkMode}
                variant="header"
                showMultiDayIndicator={false}
              />
            </div>
          );
        })}
        </div>
      </div>

      {/* Scrollable time grid - this is the main scroll container */}
      <div 
        ref={scrollContainerRef} 
        className="flex-1 overflow-y-auto overflow-x-hidden relative scrollbar-thin" 
        style={{ 
          minHeight: 0,
          height: 0, // Force flex to calculate
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
        }}
      >
        {/* Scrollable content container with fixed height */}
        <div 
          style={{ 
            position: 'relative', 
            width: '100%', 
            height: `${contentHeight}px`,
            minHeight: `${contentHeight}px`,
          }}
        >
        {/* Time column - fixed width, full height */}
        <div className="absolute left-0 top-0 w-24 z-30 pointer-events-none pr-2" style={{ height: `${contentHeight}px`, boxSizing: 'border-box' }}>
          {hours.map((hour, index) => {
            // Use fixed pixel gap between hours
            const gapPx = 4; // Fixed 4px gap between hours
            const topPositionPx = (index * baseHourHeight) + (index * gapPx);
            const heightPx = baseHourHeight;
            const bottomPositionPx = topPositionPx + heightPx;
            
            // Convert to percentage for positioning
            const hourPosition = (topPositionPx / contentHeight) * 100;
            const boxHeight = (heightPx / contentHeight) * 100;
            
            // Format time display
            const timeDisplay = hour === 0
              ? '12 AM'
              : hour < 12
              ? `${hour} AM`
              : hour === 12
              ? '12 PM'
              : `${hour - 12} PM`;
            
            return (
              <div
                key={hour}
                className={`absolute left-0 right-0 rounded-landing border-2 mb-1 ${theme.bg} ${theme.border}`}
                data-hour={hour}
                data-hour-index={index}
                data-top-percent={hourPosition.toFixed(2)}
                data-top-px={topPositionPx.toFixed(2)}
                data-height-percent={boxHeight.toFixed(2)}
                data-height-px={heightPx.toFixed(2)}
                data-bottom-px={bottomPositionPx.toFixed(2)}
                style={{
                  top: `${topPositionPx}px`,
                  height: `${heightPx}px`,
                  minHeight: '100px', // Increased to fit single-hour events with content (time, title, avatars)
                }}
              >
                <div className="h-full flex flex-col items-center justify-center p-1">
                  <div className={`${theme.textMuted} text-sm sm:text-base font-bold leading-tight text-center whitespace-nowrap`}>
                    {timeDisplay}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Current time indicator line */}
        {currentTimePosition !== null && currentTimePosition >= 0 && currentTimePosition <= 100 && (
          <div
            className="absolute left-0 right-0 z-0 pointer-events-none"
            style={{
              top: `${currentTimePosition}%`,
            }}
          >
            <div className="relative">
              {/* Peachy line */}
              <div className="absolute left-0 right-0 h-0.5 bg-[rgba(217,119,111,0.6)] shadow-lg" />
              {/* Peachy dot */}
              <div className="absolute left-0 w-3 h-3 bg-[rgba(217,119,111,0.6)] rounded-sm-full -translate-x-1.5 -translate-y-1.5 shadow-lg" />
            </div>
          </div>
        )}

        {/* Hour separator lines - in the middle of padding gaps, behind content */}
        {hours.map((hour, index) => {
          // Skip the first hour (index 0) as there's no gap before it
          if (index === 0) return null;
          
          const gapPx = 4; // Fixed 4px gap between hours
          const prevHourTopPx = ((index - 1) * baseHourHeight) + ((index - 1) * gapPx);
          const prevHourBottomPx = prevHourTopPx + baseHourHeight;
          const currentHourTopPx = (index * baseHourHeight) + (index * gapPx);
          
          // Line should be in the middle of the gap
          const linePositionPx = (prevHourBottomPx + currentHourTopPx) / 2;
          
          return (
            <div
              key={`hour-line-${hour}`}
              className="absolute left-0 right-0 z-0 pointer-events-none"
              style={{
                top: `${linePositionPx}px`,
                height: '1px',
                backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.4)',
              }}
            />
          );
        })}

        {/* Half-hour separator lines - dashed, thinner, at 30 minutes into each hour */}
        {hours.map((hour, index) => {
          const gapPx = 4; // Fixed 4px gap between hours
          const hourTopPx = (index * baseHourHeight) + (index * gapPx);
          // Half-hour mark is 30 minutes (0.5) into the hour
          const halfHourPositionPx = hourTopPx + (baseHourHeight * 0.5);
          
          return (
            <div
              key={`half-hour-line-${hour}`}
              className="absolute left-0 right-0 z-0 pointer-events-none"
              style={{
                top: `${halfHourPositionPx}px`,
                height: '1px',
                borderTop: `1px dashed ${isDarkMode ? 'rgba(148, 163, 184, 0.6)' : 'rgba(148, 163, 184, 0.7)'}`,
              }}
            />
          );
        })}

        {/* Day columns - match header positions exactly */}
        {weekDays.map((day, dayIndex) => {
          const isDateToday = isToday(day.date);
          const dayStart = new Date(day.date);
          dayStart.setHours(0, 0, 0, 0);
          const dayEnd = new Date(day.date);
          dayEnd.setHours(23, 59, 59, 999);

          // Use measured positions from header (columnPositions[dayIndex + 1] because index 0 is "Time" column)
          // Time column is w-24 (96px)
          const columnPos = columnPositions[dayIndex + 1];
          const timeColumnWidth = 96; // w-24 = 96px
          const containerWidth = scrollContainerRef.current?.clientWidth || 0;
          const availableWidth = containerWidth > 0 ? containerWidth - timeColumnWidth : 0;
          const dayColumnWidth = columnPos ? columnPos.width : (availableWidth / 7);
          const dayLeft = columnPos ? columnPos.left : (timeColumnWidth + (dayIndex * (availableWidth / 7)));

          // Calculate pixel positions for debugging
          const topPaddingSize = containerWidth < 640 ? 2 : 4;
          const dayTopPx = 0; // Always starts at top
          const dayHeightPx = contentHeight;
          const dayBottomPx = dayTopPx + dayHeightPx;
          
          return (
            <div
              key={`day-${dayIndex}`}
              className={`absolute top-0 cursor-pointer rounded-landing border-2 pl-px sm:pl-0.5 pr-0.5 sm:pr-1 pt-0.5 sm:pt-1 transition-all active:scale-95 ${
                isDateToday 
                  ? `${theme.today}` 
                  : `${theme.bg} ${theme.border}`
              }`}
              data-day-index={dayIndex}
              data-day-date={day.date.toISOString()}
              data-left-px={dayLeft.toFixed(2)}
              data-width-px={dayColumnWidth.toFixed(2)}
              data-top-px={dayTopPx.toFixed(2)}
              data-height-px={dayHeightPx.toFixed(2)}
              data-bottom-px={dayBottomPx.toFixed(2)}
              data-content-height={contentHeight.toFixed(2)}
              data-top-padding={topPaddingSize}
              style={{
                left: `${dayLeft}px`,
                width: `${dayColumnWidth}px`,
                height: `${contentHeight}px`,
                paddingBottom: 0,
                boxSizing: 'border-box', // Ensure padding and borders are included in height
              }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const y = e.clientY - rect.top;
                const hour = Math.floor((y / rect.height) * 24);
                onDateClick?.(day.date, hour);
              }}
            />
          );
        })}

        {/* Positioned events - adjust positions based on visible hours */}
        {positionedEvents
          .filter((pe, index, self) => 
            // Deduplicate by event ID and dayIndex to prevent duplicate rendering
            index === self.findIndex(p => p.event.id === pe.event.id && p.dayIndex === pe.dayIndex)
          )
          .map(({ event, dayIndex, daySpan }, eventIndex) => {
          // Skip if no visible hours
          if (hours.length === 0) return null;
          
          // Convert top/height from 24-hour day percentage to visible hours percentage
          // top and height are percentages (0-100) of a 24-hour day
          // We need to convert them to percentages of the visible hours range
          
          // Get event times in minutes (0-1440 for a 24-hour day)
          const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
          const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
          const eventStartMinutes = eventStart.getHours() * 60 + eventStart.getMinutes();
          const eventEndMinutes = eventEnd.getHours() * 60 + eventEnd.getMinutes();
          
          // Find which visible hours this event spans
          const firstVisibleHour = hours[0];
          const lastVisibleHour = hours[hours.length - 1];
          const firstVisibleHourMinutes = firstVisibleHour * 60;
          const lastVisibleHourEndMinutes = (lastVisibleHour + 1) * 60;
          
          // Check if event overlaps with visible hours (use < and > for strict overlap)
          if (eventEndMinutes <= firstVisibleHourMinutes || eventStartMinutes >= lastVisibleHourEndMinutes) {
            return null; // Event is outside visible hours
          }
          
          // Calculate positions accounting for hour block gaps
          // Each hour block has a fixed 4px gap between them
          const gapPx = 4; // Fixed 4px gap between hours
          
          // Find which hour block the event starts in
          const startHourIndex = hours.findIndex(h => {
            const hourStartMinutes = h * 60;
            const hourEndMinutes = (h + 1) * 60;
            return eventStartMinutes >= hourStartMinutes && eventStartMinutes < hourEndMinutes;
          });
          
          // Find which hour block the event ends in
          const endHourIndex = hours.findIndex(h => {
            const hourStartMinutes = h * 60;
            const hourEndMinutes = (h + 1) * 60;
            return eventEndMinutes > hourStartMinutes && eventEndMinutes <= hourEndMinutes;
          });
          
          if (startHourIndex === -1) return null; // Event doesn't start in a visible hour
          
          // Center events within the hour blocks they occupy
          // Find the first and last hour blocks the event spans
          const firstHourIndex = startHourIndex;
          const lastHourIndex = endHourIndex === -1 ? hours.length - 1 : endHourIndex;
          
          // Calculate the top of the first hour block
          const firstHourBlockTopPx = (firstHourIndex * baseHourHeight) + (firstHourIndex * gapPx);
          
          // Calculate the bottom of the last hour block
          const lastHourBlockTopPx = (lastHourIndex * baseHourHeight) + (lastHourIndex * gapPx);
          const lastHourBlockBottomPx = lastHourBlockTopPx + baseHourHeight;
          
          // Add top padding to match the gap spacing (half the gap = 2px)
          // This creates space between the hour separator and the event card
          const topPaddingPx = gapPx / 2; // 2px padding at top of hour block
          
          // Center the event vertically within the hour blocks it spans
          // The event should start below the top padding
          const newTopPx = firstHourBlockTopPx + topPaddingPx;
          const newTop = (newTopPx / contentHeight) * 100;
          
          // The event should span the full height of all hour blocks it occupies, minus top and bottom padding
          // Calculate total height: from first hour block top to last hour block bottom, minus padding
          const bottomPaddingPx = gapPx / 2; // 2px padding at bottom of hour block
          const totalHeightPx = (lastHourBlockBottomPx - firstHourBlockTopPx) - topPaddingPx - bottomPaddingPx;
          const newHeight = (totalHeightPx / contentHeight) * 100;
          
          // Use measured column positions if available, otherwise use fallback calculation
          // columnPositions[dayIndex + 1] because index 0 is "Time" column
          const startColumnPos = columnPositions[dayIndex + 1];
          const containerWidth = scrollContainerRef.current?.clientWidth || 0;
          
          // Skip rendering if container width is not available yet
          if (containerWidth === 0) return null;
          
          const timeColumnWidth = 96; // w-24 = 96px
          
          // Calculate the left position of the starting day column
          const startDayColumnLeft = startColumnPos ? startColumnPos.left : (timeColumnWidth + dayIndex * ((containerWidth - timeColumnWidth) / 7));
          const startDayColumnWidth = startColumnPos ? startColumnPos.width : ((containerWidth - timeColumnWidth) / 7);
          
          // For multi-day events, calculate the total width across all spanned days
          let totalDayColumnsWidth = startDayColumnWidth;
          if (daySpan > 1) {
            // Calculate from the left of the first column to the right of the last column
            const endDayIndex = Math.min(dayIndex + daySpan - 1, weekDays.length - 1);
            const endColumnPos = columnPositions[endDayIndex + 1];
            
            if (startColumnPos && endColumnPos) {
              // Use measured positions: right edge of last column - left edge of first column
              // This already includes gaps between columns since getBoundingClientRect() measures the actual rendered positions
              totalDayColumnsWidth = (endColumnPos.left + endColumnPos.width) - startColumnPos.left;
            } else {
              // Fallback: sum up widths and gaps
              // Header uses gap-1 sm:gap-2 (4px on mobile, 8px on larger screens)
              const gapSize = containerWidth < 640 ? 4 : 8;
              totalDayColumnsWidth = 0;
              for (let i = 0; i < daySpan; i++) {
                const currentDayIndex = dayIndex + i;
                if (currentDayIndex >= weekDays.length) break;
                const currentColumnPos = columnPositions[currentDayIndex + 1];
                const currentDayColumnWidth = currentColumnPos ? currentColumnPos.width : ((containerWidth - timeColumnWidth) / 7);
                totalDayColumnsWidth += currentDayColumnWidth;
                // Add gap after each column except the last one
                if (i < daySpan - 1) {
                  totalDayColumnsWidth += gapSize;
                }
              }
            }
          }
          
          // Skip if column width is invalid
          if (startDayColumnWidth <= 0 || totalDayColumnsWidth <= 0) return null;
          
          // Account for padding in day columns (pl-0.25 sm:pl-0.5 = 1px on mobile, 2px on larger screens for left, pr-0.5 sm:pr-1 = 2px/4px for right)
          const leftPaddingSize = containerWidth < 640 ? 1 : 2;
          const rightPaddingSize = containerWidth < 640 ? 2 : 4;
          const paddingSize = leftPaddingSize; // Use left padding for positioning calculations
          
          // Calculate the actual content area (column width minus left and right padding)
          const startColumnContentWidth = startDayColumnWidth - leftPaddingSize - rightPaddingSize;
          const totalContentWidth = daySpan > 1 
            ? totalDayColumnsWidth - leftPaddingSize - rightPaddingSize // Multi-day: subtract padding from total
            : startColumnContentWidth; // Single-day: use single column content width
          
          // ABSOLUTE RULE: If events overlap, they MUST shrink to fit and be placed side by side
          // Find ALL events on the same day that overlap with this event (check all events, not just previous ones)
          const allOverlappingEventsOnSameDay = positionedEvents
            .map((pe, idx) => ({ ...pe, originalIndex: idx }))
            .filter((pe) => {
              // Only check events on the same day
              if (pe.dayIndex !== dayIndex) return false;
              
              // Skip the current event itself
              if (pe.originalIndex === eventIndex) return false;
              
              // Get the other event's times
              const otherEventStart = pe.event.startTime instanceof Date ? pe.event.startTime : new Date(pe.event.startTime);
              const otherEventEnd = pe.event.endTime instanceof Date ? pe.event.endTime : new Date(pe.event.endTime);
              const otherStartMinutes = otherEventStart.getHours() * 60 + otherEventStart.getMinutes();
              const otherEndMinutes = otherEventEnd.getHours() * 60 + otherEventEnd.getMinutes();
              
              // Check if events overlap in time (using <= and >= to include exact matches)
              return eventStartMinutes < otherEndMinutes && eventEndMinutes > otherStartMinutes;
            });
          
          // RULE: Events on the active (current) hour are positioned first (leftmost)
          // Only apply this rule if viewing today and this is the current day
          const isViewingToday = weekDays.some(day => isToday(day.date));
          const currentDay = weekDays.find(day => isToday(day.date));
          const currentDayIndex = currentDay ? weekDays.findIndex(d => d.date.getTime() === currentDay.date.getTime()) : -1;
          const isCurrentDay = isViewingToday && dayIndex === currentDayIndex;
          
          // Get current hour in minutes for comparison (only if viewing today and this is today)
          const currentHour = currentTime.getHours();
          const currentHourMinutes = currentHour * 60;
          
          // Helper function to check if an event contains the current hour
          const containsCurrentHour = (eventStartMins: number, eventEndMins: number): boolean => {
            if (!isCurrentDay) return false;
            return eventStartMins <= currentHourMinutes && eventEndMins > currentHourMinutes;
          };
          
          // Add start/end minutes to all overlapping events for easier sorting
          const overlappingEventsWithTimes = allOverlappingEventsOnSameDay.map(pe => {
            const otherEventStart = pe.event.startTime instanceof Date ? pe.event.startTime : new Date(pe.event.startTime);
            const otherEventEnd = pe.event.endTime instanceof Date ? pe.event.endTime : new Date(pe.event.endTime);
            return {
              ...pe,
              startMinutes: otherEventStart.getHours() * 60 + otherEventStart.getMinutes(),
              endMinutes: otherEventEnd.getHours() * 60 + otherEventEnd.getMinutes()
            };
          });
          
          // Calculate position index within the overlapping group
          // Sort overlapping events: events on active hour first, then by start time
          const sortedOverlappingEvents = [...overlappingEventsWithTimes, { 
            event, 
            dayIndex, 
            originalIndex: eventIndex,
            startMinutes: eventStartMinutes,
            endMinutes: eventEndMinutes
          }].sort((a, b) => {
            // Priority 1: Events on active hour come first
            const aOnActiveHour = containsCurrentHour(a.startMinutes, a.endMinutes);
            const bOnActiveHour = containsCurrentHour(b.startMinutes, b.endMinutes);
            
            if (aOnActiveHour && !bOnActiveHour) return -1; // a comes first
            if (!aOnActiveHour && bOnActiveHour) return 1;  // b comes first
            
            // Priority 2: If both or neither are on active hour, sort by start time
            if (a.startMinutes !== b.startMinutes) {
              return a.startMinutes - b.startMinutes;
            }
            
            // Priority 3: If same start time, sort by end time (longer events first)
            if (a.endMinutes !== b.endMinutes) {
              return b.endMinutes - a.endMinutes; // Longer events first
            }
            
            // Priority 4: If still tied, sort by event ID for consistency
            return (a.event.id || '').localeCompare(b.event.id || '');
          });
          
          // Find this event's position in the sorted group
          // Use event ID for reliable matching since originalIndex might not be unique
          const positionInGroup = sortedOverlappingEvents.findIndex(pe => pe.event.id === event.id);
          
          // If still not found, try originalIndex as fallback
          const actualPositionInGroup = positionInGroup !== -1 
            ? positionInGroup 
            : sortedOverlappingEvents.findIndex(pe => pe.originalIndex === eventIndex);
          
          // Safety check: if position not found, this event doesn't overlap with others - use full width
          if (actualPositionInGroup === -1) {
            // No overlaps - event gets full width at left edge
            // Use consistent width calculation for all event types
            const baseContentWidth = daySpan > 1 ? totalContentWidth : startColumnContentWidth;
            const finalLeftPx = startDayColumnLeft + paddingSize;
            const finalWidthPx = baseContentWidth;
            
          // Account for day column top padding (pt-0.5 sm:pt-1 = 2px on mobile, 4px on larger screens)
          const topPaddingSize = containerWidth < 640 ? 2 : 4;
          const eventIsPast = isEventPast(event);
          // Reduce height for past events (multiply by 0.7)
          const adjustedHeight = eventIsPast ? newHeight * 0.7 : newHeight;
          const minEventHeight = eventIsPast ? 70 : 100;
          
          // Calculate top position: newTop is a percentage (0-100) that accounts for gaps
          // Convert to pixels using contentHeight (which includes gap space), then add top padding
          const topPositionPx = (newTop / 100) * contentHeight + topPaddingSize;
          // newHeight is also a percentage that accounts for gaps
          const heightPx = Math.max((adjustedHeight / 100) * contentHeight, minEventHeight);
          
          return (
            <div
              key={`${event.id}-${dayIndex}`}
              className={`absolute overflow-hidden ${eventIsPast ? 'opacity-70' : ''}`}
              style={{
                top: `${topPositionPx}px`,
                height: `${heightPx}px`,
                left: `${finalLeftPx}px`,
                width: `${finalWidthPx}px`,
                zIndex: eventIsPast ? 1 : 2, // Past events behind upcoming
              }}
            >
              <EventCard
                event={event}
                onEventClick={onEventClick}
                isDarkMode={isDarkMode}
                size="sm"
                showMultiDayIndicator={false}
                isPast={eventIsPast}
              />
            </div>
          );
          }
          
          // Recalculate horizontal positioning: events MUST shrink to fit side by side
          const groupSize = sortedOverlappingEvents.length; // Total number of overlapping events including this one
          const horizontalGap = 1; // 1% gap between side-by-side events
          const totalGap = (groupSize - 1) * horizontalGap;
          const availableWidth = 100 - totalGap;
          const recalculatedWidth = availableWidth / groupSize; // Each event gets equal width
          const recalculatedLeft = actualPositionInGroup * (recalculatedWidth + horizontalGap);
          
          // Use recalculated positioning - ensure events are side by side
          // recalculatedLeft is a percentage (0-100), convert to pixels within the content area
          // For overlapping events on the same day, ALL events must use the same base width
          // This ensures single-hour and multi-hour events have consistent widths when overlapping
          // Only use totalContentWidth for multi-day events (daySpan > 1), otherwise use startColumnContentWidth
          const baseContentWidth = daySpan > 1 ? totalContentWidth : startColumnContentWidth;
          const finalLeftPx = startDayColumnLeft + paddingSize + (recalculatedLeft * startColumnContentWidth / 100);
          // recalculatedWidth is also a percentage (0-100), convert to pixels
          // CRITICAL: Use startColumnContentWidth for all overlapping events on the same day
          // This ensures consistent width regardless of whether event is single-hour or multi-hour
          const finalWidthPx = startColumnContentWidth * (recalculatedWidth / 100);
          
          // Ensure minimum width so events are visible
          if (finalWidthPx < 20) {
            // If width is too small, still show it but with minimum width
            // This shouldn't happen with proper overlap detection, but safety check
          }
          
          // Account for day column top padding (pt-0.5 sm:pt-1 = 2px on mobile, 4px on larger screens)
          const topPaddingSize = containerWidth < 640 ? 2 : 4;
          
          // Check if event is past and adjust accordingly
          const eventIsPast = isEventPast(event);
          // Reduce height for past events (multiply by 0.7)
          const adjustedHeight = eventIsPast ? newHeight * 0.7 : newHeight;
          // Calculate minimum height needed for content (reduced for past events)
          const minEventHeight = eventIsPast ? 70 : 100;
          
          // Calculate top position: percentage of content height, then add padding
          // But ensure it doesn't exceed the content area
          const contentAreaHeight = contentHeight - (topPaddingSize * 2);
          const topPositionPx = (newTop / 100) * contentAreaHeight + topPaddingSize;
          const heightPx = Math.max((adjustedHeight / 100) * contentAreaHeight, minEventHeight);
          
          return (
            <div
              key={`${event.id}-${dayIndex}`}
              className={`absolute overflow-hidden ${eventIsPast ? 'opacity-70' : ''}`}
              style={{
                top: `${topPositionPx}px`,
                height: `${heightPx}px`,
                left: `${finalLeftPx}px`,
                width: `${finalWidthPx}px`,
                zIndex: eventIsPast ? 1 : 2, // Past events behind upcoming
              }}
            >
              <EventCard
                event={event}
                onEventClick={onEventClick}
                isDarkMode={isDarkMode}
                size="sm"
                showMultiDayIndicator={false}
                isPast={eventIsPast}
              />
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
